import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Save, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  Type, AlignLeft, Calendar, Clock, ListChecks, CheckSquare, Mail, Hash, Image
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text', icon: Type },
  { value: 'textarea', label: 'Long Text', icon: AlignLeft },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'time', label: 'Time', icon: Clock },
  { value: 'multiselect', label: 'Checkboxes (Multiple)', icon: ListChecks },
  { value: 'select', label: 'Dropdown (Single)', icon: ChevronDown },
  { value: 'image', label: 'Image Upload', icon: Image },
];

export default function FormBuilderPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    category: 'logistics',
    icon: 'clipboard',
    is_active: true,
    requires_signature: false,
    allow_draft: true,
    sections: []
  });
  const [loading, setLoading] = useState(!!templateId);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    }
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/forms/templates/${templateId}`, { headers });
      setTemplate(res.data);
      // Expand all sections by default
      const expanded = {};
      res.data.sections?.forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
    } catch (err) {
      console.error('Failed to fetch template:', err);
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template.name.trim()) {
      toast.error('Please enter a form name');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (templateId) {
        await axios.put(`${API}/forms/templates/${templateId}`, template, { headers });
        toast.success('Template updated');
      } else {
        const res = await axios.post(`${API}/forms/templates`, template, { headers });
        toast.success('Template created');
        navigate(`/dashboard/forms/builder/${res.data.id}`);
      }
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    const newSection = {
      id: uuidv4(),
      title: 'New Section',
      description: '',
      order: template.sections.length + 1,
      fields: []
    };
    setTemplate(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setExpandedSections(prev => ({ ...prev, [newSection.id]: true }));
  };

  const updateSection = (sectionId, updates) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, ...updates } : s
      )
    }));
  };

  const deleteSection = (sectionId) => {
    if (!confirm('Delete this section and all its fields?')) return;
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
  };

  const moveSection = (sectionId, direction) => {
    const index = template.sections.findIndex(s => s.id === sectionId);
    if ((direction === 'up' && index === 0) || 
        (direction === 'down' && index === template.sections.length - 1)) return;

    const newSections = [...template.sections];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[swapIndex]] = [newSections[swapIndex], newSections[index]];
    
    // Update order values
    newSections.forEach((s, i) => { s.order = i + 1; });
    
    setTemplate(prev => ({ ...prev, sections: newSections }));
  };

  const addField = (sectionId) => {
    const section = template.sections.find(s => s.id === sectionId);
    const newField = {
      id: uuidv4(),
      type: 'text',
      label: 'New Field',
      required: false,
      placeholder: '',
      order: (section?.fields?.length || 0) + 1,
      options: []
    };
    updateSection(sectionId, {
      fields: [...(section?.fields || []), newField]
    });
  };

  const updateField = (sectionId, fieldId, updates) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          fields: s.fields.map(f => 
            f.id === fieldId ? { ...f, ...updates } : f
          )
        };
      })
    }));
  };

  const deleteField = (sectionId, fieldId) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          fields: s.fields.filter(f => f.id !== fieldId)
        };
      })
    }));
  };

  const moveField = (sectionId, fieldId, direction) => {
    const section = template.sections.find(s => s.id === sectionId);
    const index = section.fields.findIndex(f => f.id === fieldId);
    if ((direction === 'up' && index === 0) || 
        (direction === 'down' && index === section.fields.length - 1)) return;

    const newFields = [...section.fields];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newFields[index], newFields[swapIndex]] = [newFields[swapIndex], newFields[index]];
    newFields.forEach((f, i) => { f.order = i + 1; });
    
    updateSection(sectionId, { fields: newFields });
  };

  const addOption = (sectionId, fieldId) => {
    const section = template.sections.find(s => s.id === sectionId);
    const field = section?.fields?.find(f => f.id === fieldId);
    const options = field?.options || [];
    updateField(sectionId, fieldId, {
      options: [...options, { label: 'New Option', value: uuidv4().slice(0, 8) }]
    });
  };

  const updateOption = (sectionId, fieldId, optionIndex, updates) => {
    const section = template.sections.find(s => s.id === sectionId);
    const field = section?.fields?.find(f => f.id === fieldId);
    const options = [...(field?.options || [])];
    options[optionIndex] = { ...options[optionIndex], ...updates };
    updateField(sectionId, fieldId, { options });
  };

  const deleteOption = (sectionId, fieldId, optionIndex) => {
    const section = template.sections.find(s => s.id === sectionId);
    const field = section?.fields?.find(f => f.id === fieldId);
    const options = [...(field?.options || [])];
    options.splice(optionIndex, 1);
    updateField(sectionId, fieldId, { options });
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="form-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/forms')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              {templateId ? 'Edit Template' : 'Create Template'}
            </h1>
            <p className="text-sm text-muted-foreground">Design your form structure</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Template'}
        </Button>
      </div>

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Form Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Form Name *</Label>
              <Input
                value={template.name}
                onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Field Level Hazard Assessment"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <select
                value={template.category}
                onChange={(e) => setTemplate(prev => ({ ...prev, category: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                <option value="logistics">Logistics</option>
                <option value="safety">Safety</option>
                <option value="operations">Operations</option>
                <option value="hr">Human Resources</option>
                <option value="maintenance">Maintenance</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={template.description || ''}
              onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this form's purpose"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Sections ({template.sections?.length || 0})</h2>
          <Button onClick={addSection} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        </div>

        {template.sections?.sort((a, b) => a.order - b.order).map((section, sIndex) => (
          <Card key={section.id} className="border-l-4 border-l-primary">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="flex items-center gap-2 text-left flex-1"
                >
                  {expandedSections[section.id] ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronUp className="h-5 w-5" />
                  )}
                  <span className="font-semibold">{section.title}</span>
                  <span className="text-sm text-muted-foreground">
                    ({section.fields?.length || 0} fields)
                  </span>
                </button>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => moveSection(section.id, 'up')}
                    disabled={sIndex === 0}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => moveSection(section.id, 'down')}
                    disabled={sIndex === template.sections.length - 1}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteSection(section.id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            {expandedSections[section.id] && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Section Title</Label>
                    <Input
                      value={section.title}
                      onChange={(e) => updateSection(section.id, { title: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optional)</Label>
                    <Input
                      value={section.description || ''}
                      onChange={(e) => updateSection(section.id, { description: e.target.value })}
                    />
                  </div>
                </div>

                {/* Fields */}
                <div className="space-y-3 mt-4">
                  <div className="flex items-center justify-between">
                    <Label>Fields</Label>
                    <Button size="sm" variant="outline" onClick={() => addField(section.id)}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Field
                    </Button>
                  </div>

                  {section.fields?.sort((a, b) => a.order - b.order).map((field, fIndex) => (
                    <div
                      key={field.id}
                      className="p-3 rounded-lg bg-secondary/30 border space-y-3"
                    >
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 grid grid-cols-4 gap-2">
                          <Input
                            value={field.label}
                            onChange={(e) => updateField(section.id, field.id, { label: e.target.value })}
                            placeholder="Field label"
                            className="col-span-2"
                          />
                          <select
                            value={field.type}
                            onChange={(e) => updateField(section.id, field.id, { type: e.target.value })}
                            className="h-10 px-2 rounded-md border border-input bg-background text-sm"
                          >
                            {FIELD_TYPES.map(ft => (
                              <option key={ft.value} value={ft.value}>{ft.label}</option>
                            ))}
                          </select>
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-sm">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => updateField(section.id, field.id, { required: e.target.checked })}
                                className="rounded"
                              />
                              Required
                            </label>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => moveField(section.id, field.id, 'up')}
                            disabled={fIndex === 0}
                            className="h-8 w-8"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => moveField(section.id, field.id, 'down')}
                            disabled={fIndex === section.fields.length - 1}
                            className="h-8 w-8"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteField(section.id, field.id)}
                            className="h-8 w-8 text-red-500"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Options for multiselect/select */}
                      {(field.type === 'multiselect' || field.type === 'select') && (
                        <div className="pl-6 space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Options</Label>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => addOption(section.id, field.id)}
                              className="h-6 text-xs"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>
                          {field.options?.map((opt, oIndex) => (
                            <div key={oIndex} className="flex items-center gap-2">
                              <Input
                                value={opt.label}
                                onChange={(e) => updateOption(section.id, field.id, oIndex, { 
                                  label: e.target.value,
                                  value: e.target.value.toLowerCase().replace(/\s+/g, '_')
                                })}
                                placeholder="Option label"
                                className="h-8 text-sm"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteOption(section.id, field.id, oIndex)}
                                className="h-8 w-8 text-red-500"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Placeholder for text fields */}
                      {['text', 'textarea', 'email', 'number'].includes(field.type) && (
                        <div className="pl-6">
                          <Input
                            value={field.placeholder || ''}
                            onChange={(e) => updateField(section.id, field.id, { placeholder: e.target.value })}
                            placeholder="Placeholder text (optional)"
                            className="h-8 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  ))}

                  {(!section.fields || section.fields.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No fields yet. Click "Add Field" to start.
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        ))}

        {(!template.sections || template.sections.length === 0) && (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground mb-4">No sections yet</p>
              <Button onClick={addSection}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Section
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
